import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Facebook, Instagram, Send, Trash2, Loader2, Link2, Wand2, Trophy, Download } from "lucide-react";

const THEME_LABELS = {
    first_month_free: "First month free",
    esim: "eSIM activation",
    keep_your_number: "Keep your number",
    unlimited: "Unlimited talk & text",
    pricing: "Pricing / plans",
    brand: "Brand awareness",
};

function MetaConnect({ status, onChanged }) {
    const [form, setForm] = useState({ app_id: "", app_secret: "", user_token: "" });
    const [busy, setBusy] = useState(false);

    const connect = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/marketing/meta/connect", form);
            toast.success(`Connected ${data.pages.length} page(s)`);
            setForm({ app_id: "", app_secret: "", user_token: "" });
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not connect to Meta");
        }
        setBusy(false);
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect all Facebook Pages?")) return;
        try {
            await api.post("/admin/marketing/meta/disconnect");
            toast.success("Disconnected");
            onChanged();
        } catch { toast.error("Could not disconnect"); }
    };

    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="meta-connect-card">
            <div className="flex items-center gap-2 mb-3">
                <Facebook className="w-4 h-4 text-[#4C1D95]" />
                <h3 className="font-display text-lg">Connect Facebook &amp; Instagram</h3>
            </div>
            {status?.configured ? (
                <div data-testid="meta-connected">
                    <p className="text-sm text-zinc-600 mb-2">Connected pages:</p>
                    <ul className="space-y-1 mb-3">
                        {status.pages.map((p) => (
                            <li key={p.page_id} className="text-sm flex items-center gap-2" data-testid={`meta-page-${p.page_id}`}>
                                <Facebook className="w-3.5 h-3.5 text-[#4C1D95]" /> {p.name}
                                {p.has_instagram && <span className="inline-flex items-center gap-1 text-xs text-pink-600"><Instagram className="w-3.5 h-3.5" /> {p.ig_username || "IG linked"}</span>}
                            </li>
                        ))}
                    </ul>
                    <Button onClick={disconnect} variant="outline" data-testid="meta-disconnect-btn" className="rounded-sm h-9 text-xs border-[#DDD6FE]">Disconnect</Button>
                </div>
            ) : (
                <div className="space-y-2">
                    <p className="text-xs text-zinc-500">Paste your Meta app credentials + a user access token (with pages_manage_posts, pages_show_list, instagram_content_publish). We exchange it for a long-lived token and pull your Pages.</p>
                    <Input placeholder="Meta App ID" value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} data-testid="meta-app-id" />
                    <Input placeholder="Meta App Secret" value={form.app_secret} onChange={(e) => setForm({ ...form, app_secret: e.target.value })} data-testid="meta-app-secret" />
                    <Textarea placeholder="User access token" value={form.user_token} onChange={(e) => setForm({ ...form, user_token: e.target.value })} data-testid="meta-user-token" />
                    <Button onClick={connect} disabled={busy} data-testid="meta-connect-btn" className="bg-[#4C1D95] text-white rounded-sm h-9 text-xs">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Link2 className="w-4 h-4 mr-1" /> Connect</>}
                    </Button>
                </div>
            )}
        </div>
    );
}

function ContentCard({ c, status, images, onChanged }) {
    const [caption, setCaption] = useState(c.caption);
    const [hashtags, setHashtags] = useState(c.hashtags);
    const [imageKey, setImageKey] = useState(c.image_key);
    const [busy, setBusy] = useState(false);
    const img = images.find((i) => i.key === imageKey) || { url: c.image_url };

    const save = async () => {
        try {
            await api.patch(`/admin/marketing/content/${c.content_id}`, { caption, hashtags, image_key: imageKey });
            toast.success("Saved");
            onChanged();
        } catch { toast.error("Could not save"); }
    };

    const publish = async () => {
        if (!status?.configured) { toast.error("Connect a Facebook Page first"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/marketing/content/${c.content_id}/publish`, { crosspost_instagram: true });
            if (data.ok) toast.success("Published to Facebook / Instagram");
            else toast.error("Publish failed — check Meta connection");
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Publish failed");
        }
        setBusy(false);
    };

    const copyText = () => {
        navigator.clipboard.writeText(`${caption}\n\n${hashtags}`);
        toast.success("Caption copied");
    };

    const del = async () => {
        try { await api.delete(`/admin/marketing/content/${c.content_id}`); onChanged(); }
        catch { toast.error("Could not delete"); }
    };

    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-4 flex flex-col sm:flex-row gap-4" data-testid={`content-${c.content_id}`}>
            <div className="shrink-0">
                <img src={img.url} alt="" className="w-32 h-32 object-cover rounded-sm border border-[#DDD6FE]" />
                <select value={imageKey} onChange={(e) => setImageKey(e.target.value)} data-testid={`content-image-${c.content_id}`}
                    className="mt-2 w-32 text-xs border border-[#DDD6FE] rounded-sm p-1 bg-white">
                    {images.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
                </select>
            </div>
            <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border border-[#DDD6FE] text-zinc-500">{THEME_LABELS[c.theme] || c.theme}</span>
                    <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 rounded-sm border ${c.status === "published" ? "text-emerald-deep border-emerald-deep" : "text-zinc-500 border-[#DDD6FE]"}`} data-testid={`content-status-${c.content_id}`}>{c.status}</span>
                </div>
                <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} data-testid={`content-caption-${c.content_id}`} className="text-sm" />
                <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} data-testid={`content-hashtags-${c.content_id}`} className="text-xs" />
                <div className="flex flex-wrap gap-2">
                    <Button onClick={save} variant="outline" className="rounded-sm h-8 text-xs border-[#DDD6FE]" data-testid={`content-save-${c.content_id}`}>Save</Button>
                    <Button onClick={copyText} variant="outline" className="rounded-sm h-8 text-xs border-[#DDD6FE]" data-testid={`content-copy-${c.content_id}`}>Copy text</Button>
                    <Button onClick={publish} disabled={busy} className="bg-[#4C1D95] text-white rounded-sm h-8 text-xs" data-testid={`content-publish-${c.content_id}`}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Publish now</>}
                    </Button>
                    <Button onClick={del} variant="outline" className="rounded-sm h-8 text-xs border-red-200 text-red-600" data-testid={`content-delete-${c.content_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
                {c.published_results && (
                    <p className="text-xs text-zinc-500" data-testid={`content-result-${c.content_id}`}>
                        {c.published_results.map((r) => `${r.name}: FB ${r.facebook?.ok ? "✓" : "✗"}${r.instagram ? ` · IG ${r.instagram.ok ? "✓" : "✗"}` : ""}`).join("  |  ")}
                    </p>
                )}
            </div>
        </div>
    );
}

function ContestEntries() {
    const [entries, setEntries] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try { const { data } = await api.get("/admin/promo/entries"); setEntries(data.entries || []); }
        catch { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const draw = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/promo/draw", { promo_key: "iphone_contest" });
            toast.success(`🏆 Winner: ${data.winner.email}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not draw a winner"); }
        setBusy(false);
    };

    const exportCsv = () => {
        const rows = [["email", "name", "promo", "entered", "winner"], ...entries.map((e) => [e.email, e.name || "", e.promo_key, e.created_at, e.winner ? "yes" : ""])];
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a"); a.href = url; a.download = "flexline-contest-entries.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="contest-entries-card">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-[#D97706]" />
                    <h3 className="font-display text-lg">Contest entries <span className="text-zinc-400 text-sm">({entries.length})</span></h3>
                </div>
                <div className="flex gap-2">
                    <Button onClick={exportCsv} variant="outline" data-testid="contest-export-btn" className="rounded-sm h-9 text-xs border-[#DDD6FE]"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
                    <Button onClick={draw} disabled={busy || entries.length === 0} data-testid="contest-draw-btn" className="bg-[#D97706] text-white rounded-sm h-9 text-xs">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Trophy className="w-3.5 h-3.5 mr-1" /> Draw a winner</>}
                    </Button>
                </div>
            </div>
            {entries.length === 0 ? (
                <p className="text-sm text-zinc-500 py-6 text-center" data-testid="contest-empty">No entries yet — the "Win an iPhone" pop-up captures them here.</p>
            ) : (
                <div className="max-h-64 overflow-y-auto divide-y divide-[#EFE9F8]">
                    {entries.map((e) => (
                        <div key={e.entry_id} className="py-2 flex items-center justify-between text-sm" data-testid={`contest-entry-${e.entry_id}`}>
                            <div className="min-w-0">
                                <div className="truncate">{e.email} {e.winner && <span className="ml-1 text-[10px] font-mono uppercase text-[#D97706] border border-[#D97706] rounded-sm px-1">Winner</span>}</div>
                                <div className="font-mono text-[10px] text-zinc-400">{e.promo_key} · {new Date(e.created_at).toLocaleDateString()}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function MarketingStudio() {
    const [status, setStatus] = useState(null);
    const [content, setContent] = useState([]);
    const [images, setImages] = useState([]);
    const [themes, setThemes] = useState([]);
    const [theme, setTheme] = useState("first_month_free");
    const [count, setCount] = useState(2);
    const [extra, setExtra] = useState("");
    const [generating, setGenerating] = useState(false);

    const load = async () => {
        try {
            const [st, ct] = await Promise.all([
                api.get("/admin/marketing/meta/status"),
                api.get("/admin/marketing/content"),
            ]);
            setStatus(st.data);
            setContent(ct.data.content);
            setImages(ct.data.images);
            setThemes(ct.data.themes);
        } catch { /* ignore */ }
    };

    useEffect(() => { load(); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            await api.post("/admin/marketing/generate", { theme, count: Number(count), extra_instructions: extra || undefined });
            toast.success("Content generated");
            setExtra("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Generation failed");
        }
        setGenerating(false);
    };

    return (
        <div className="space-y-4" data-testid="admin-studio">
            <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-[#DDD6FE] rounded-sm bg-paper p-5" data-testid="generate-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-[#D97706]" />
                        <h3 className="font-display text-lg">Generate marketing content</h3>
                    </div>
                    <label className="text-xs text-zinc-500">Campaign theme</label>
                    <select value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="studio-theme"
                        className="w-full text-sm border border-[#DDD6FE] rounded-sm p-2 bg-white mb-2">
                        {themes.map((t) => <option key={t} value={t}>{THEME_LABELS[t] || t}</option>)}
                    </select>
                    <label className="text-xs text-zinc-500">How many variations (1-5)</label>
                    <Input type="number" min={1} max={5} value={count} onChange={(e) => setCount(e.target.value)} data-testid="studio-count" className="mb-2" />
                    <Textarea placeholder="Optional extra direction (e.g. mention students, or a holiday)" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="studio-extra" className="mb-2 text-sm" />
                    <Button onClick={generate} disabled={generating} data-testid="studio-generate-btn" className="bg-[#D97706] text-white rounded-sm w-full">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wand2 className="w-4 h-4 mr-1" /> Generate with AI</>}
                    </Button>
                </div>
                <MetaConnect status={status} onChanged={load} />
            </div>

            <ContestEntries />

            <h3 className="font-display text-xl pt-2">Content calendar</h3>
            {content.length === 0 ? (
                <div className="border border-[#DDD6FE] rounded-sm bg-paper p-10 text-center text-sm text-zinc-500" data-testid="studio-empty">No content yet — generate your first posts above.</div>
            ) : (
                <div className="space-y-3">
                    {content.map((c) => (
                        <ContentCard key={c.content_id} c={c} status={status} images={images} onChanged={load} />
                    ))}
                </div>
            )}
        </div>
    );
}
